import { getLiveChannels, playVideo, setVideoOptions, translationPacket } from 'common/actions';
import ChannelsCategorySelectorView from 'common/components/channels-category-selector-view/ChannelsCategorySelectorView';
import { LiveChannelListItem } from 'common/components/live-channel-list-item';
import { PaidChannelPopup } from 'common/components/message-popups';
import { Stripe } from 'common/components/stripe';
import TVChannelsBannerBackground from 'common/components/tv-channel-banner-background/TVChannelsBannerBackground';
import { ViewWithDetails } from 'common/components/view-with-details';
import { ViewWithDetailsRefObject } from 'common/components/view-with-details/ViewWithDetails';
import variables from 'common/config/variables';
import {
  Category,
  Epg,
  IViewWithDetailsStripeItem,
  LiveChannel,
  LiveChannels
} from 'common/constants/data-types';
import { usePopup } from 'common/contexts/popup';
import protect from 'common/HoC/protect';
import { AppTheme } from 'common/interfaces';
import { FavoriteChannels } from 'common/reducers/favoriteChannels';
import { IVideoPlayer } from 'common/reducers/videoPlayer';
import { useParentalLock, usePlayerActions } from 'common/utils/hooks';
import { usePinPopup } from 'common/utils/hooks/usePinPopup';
import { PromiseDispatch } from 'index';
import { isEmpty } from 'lodash';
import React, {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState
} from 'react';
import { connect } from 'react-redux';
const { STRIPE_PADDING } = variables;

interface StateProps {
  liveChannels: LiveChannels;
  favoriteChannels: FavoriteChannels;
  logos: any;
  i18n: typeof translationPacket;
  setVideoOptions: any;
  playVideo: (currentepg: Epg, options?: any) => void;
  videoPlayer: IVideoPlayer;
  getLiveChannels: PromiseDispatch;
}

interface TVChannelsProps extends StateProps {
  currentPlayingChannelId?: string;
}

function TVChannels(props: TVChannelsProps) {
  const viewWithDetailsRef = React.useRef<ViewWithDetailsRefObject>(null);
  const [currentCategory, setCurrentCategory] = useState<Partial<Category> | null>(null);
  const deferredCategory = useDeferredValue(currentCategory);
  const { openPopup } = usePopup();
  const { openPinPopup } = usePinPopup();
  const { getChannelsLockedState, unlock: temporarilyUnlock } = useParentalLock();
  const { playVideoFromContinueWatching: playVideo } = usePlayerActions();

  useEffect(() => {
    const id = requestIdleCallback(
      () => {
        props.getLiveChannels();
      },
      { timeout: variables.THROTTLE_TIMEOUT }
    );
    return () => {
      cancelIdleCallback(id);
    };
  }, []);

  const hideFavoriteChannels = useMemo(
    () =>
      isEmpty(props.favoriteChannels) ||
      (variables.WHITELABEL_TYPE === AppTheme.LINEAR && !deferredCategory),
    [props.favoriteChannels, deferredCategory]
  );

  const formattedChannels = useMemo(() => {
    if (isEmpty(props.liveChannels)) return [];
    const channels = [
      ...(hideFavoriteChannels ? [] : Object.values(props.favoriteChannels)),
      ...Object.values(props.liveChannels)
    ];
    return channels
      .filter((e) => e.visible)
      .filter((e) => (deferredCategory ? e.category === deferredCategory.position : true))
      .map((channel: LiveChannel) => ({
        ...channel,
        isLiveCard: true
      }));
  }, [props.liveChannels, deferredCategory]);

  // TODO: make a generic function for this
  const openPlayer = useCallback(
    (channel: IViewWithDetailsStripeItem) => {
      const video = channel.isLiveCard ? channel.currentepg : channel;
      const channelsLockedState = getChannelsLockedState();
      const playingSameChannel = props.currentPlayingChannelId === video.chan_id;

      const handlePlay = () => {
        playVideo(video, {
          channelPosition: channel.position,
          isFullscreen: true,
          continueWatching: true
        });
      };

      if (!channel.access) {
        openPopup({
          id: 'paid-channel-popup',
          type: 'primary',
          content: <PaidChannelPopup popupId="paid-channel-popup" />
        });
      } else if (channel.locked && channelsLockedState && !playingSameChannel) {
        openPinPopup({
          onSuccess: () => {
            temporarilyUnlock();
            handlePlay();
          }
        });
      } else {
        handlePlay();
      }
    },
    [props.currentPlayingChannelId]
  );

  const renderLiveListItem = useCallback(
    (item: LiveChannel, index: number) => {
      return (
        <LiveChannelListItem
          current={item.currentepg}
          next={item.nextepg}
          logo={props.logos[item.id]?.pngWhite}
          i18n={props.i18n}
          progress={item.progress}
          position={item.position}
          locked={item.locked}
          adult={item.adult}
          thumbnail={item.thumbnail}
          testId={`live-channel-list-item-${index}`}
        />
      );
    },
    [props.i18n, deferredCategory]
  );

  const renderLiveChannelList = useCallback(() => {
    return (
      <Stripe
        onStripeItemPress={openPlayer}
        items={formattedChannels}
        renderItem={renderLiveListItem}
        testId="tv-channel-stripe"
      />
    );
  }, [formattedChannels]);

  useEffect(() => {
    if (viewWithDetailsRef.current?.currentAsset && !isEmpty(props.liveChannels)) {
      const current = props.liveChannels[viewWithDetailsRef.current?.currentAsset.epg.chan_id];
      startTransition(() =>
        viewWithDetailsRef.current?.setDetailedInfoAsset({
          epg: current?.currentepg,
          isLiveCard: true,
          resolution: current?.resolution
        })
      );
    }
  }, [props.liveChannels]);

  const getSelectedAssetData = useCallback(
    (asset: IViewWithDetailsStripeItem) => {
      if (asset && !isEmpty(props.liveChannels)) {
        const epg = props.liveChannels[asset.id]?.currentepg;

        // if adult channel return undefined
        if (props.liveChannels[asset.id]?.adult) {
          return undefined;
        }

        const newAsset = {
          epg,
          isLiveCard: !!asset.isLiveCard,
          resolution: asset.isLiveCard ? asset.resolution : props.liveChannels[asset.id]?.resolution
        };
        return epg && newAsset;
      } else {
        return undefined;
      }
    },
    [props.liveChannels]
  );

  return (
    <ViewWithDetails
      ref={viewWithDetailsRef}
      focusKey={location.pathname}
      disableBanners
      customBannerView={<TVChannelsBannerBackground />}
      axis="y"
      stripeAlign="center"
      stripeScrollPadding={STRIPE_PADDING}
      getSelectedAssetData={getSelectedAssetData}
      stripeItemClassName="tv-channel-item"
      stripeHeader={
        <ChannelsCategorySelectorView
          currentCategory={deferredCategory}
          onCategorySelect={setCurrentCategory}
        />
      }
    >
      {renderLiveChannelList()}
    </ViewWithDetails>
  );
}

const mapStateToProps = ({
  liveChannels,
  i18n,
  logos,
  favoriteChannels,
  videoPlayer
}: StateProps) => ({
  liveChannels,
  favoriteChannels,
  i18n,
  logos,
  currentPlayingChannelId: videoPlayer.channelId
});
getLiveChannels;
export default React.memo(
  protect(
    connect(mapStateToProps, {
      playVideo,
      getLiveChannels,
      setVideoOptions
    })(TVChannels)
  )
);
